import { ShaderStore } from "@babylonjs/core";
import { lineVertexShader } from "@babylonjs/core/Shaders/line.vertex.js";
import { lineVertexShaderWGSL } from "@babylonjs/core/ShadersWGSL/line.vertex.js";

/**
 * Two fixes to Babylon's edge (ink line) shader, which projects both ends of a segment and
 * extrudes the line across the direction between them:
 *
 * - Near clip. An end behind the camera projects mirrored, so a long edge that passes behind the
 *   camera (common on big batched meshes) turns into a stray stroke across the screen. Each
 *   segment is clipped to just in front of the camera before it's projected.
 * - Minimum width. The extrusion is in clip space, so lines thin with distance like a pen held at
 *   arm's length, and far past ~30 m they fall under a pixel and break up into dots. They keep
 *   their taper up close but never draw thinner than about a pixel (MIN is a fraction of the
 *   screen's height: 0.0026 is 1.3 px on a 1000 px tall view).
 *
 * The shaders are registered eagerly (as materials.ts does for the cel shader), patched once.
 */
const NEAR = "0.05";
const MIN = "0.0026";
const CLIP = `if (viewPosition.w < ${NEAR} && viewPositionNext.w > ${NEAR}) { viewPosition = mix(viewPosition, viewPositionNext, (${NEAR} - viewPosition.w) / (viewPositionNext.w - viewPosition.w)); } else if (viewPositionNext.w < ${NEAR} && viewPosition.w > ${NEAR}) { viewPositionNext = mix(viewPositionNext, viewPosition, (${NEAR} - viewPositionNext.w) / (viewPosition.w - viewPositionNext.w)); }\n`;

function patch(store: Record<string, string>, name: string, source: string, edits: { anchor: string; before?: string; after?: string }[]) {
  let s = store[name] ?? source;
  if (s.includes("/*ink*/")) return;
  for (const e of edits) {
    if (!s.includes(e.anchor)) {
      console.warn(`ink: ${name} changed shape; stray or dotted ink strokes may return`);
      store[name] = store[name] ?? source;
      return;
    }
    s = s.replace(e.anchor, `${e === edits[0] ? "/*ink*/" : ""}${e.before ?? ""}${e.anchor}${e.after ?? ""}`);
  }
  store[name] = s;
}

patch(ShaderStore.ShadersStore, lineVertexShader.name, lineVertexShader.shader, [
  { anchor: "vec2 currentScreen=", before: CLIP },
  { anchor: "vec4 offset=vec4(normalDir*normal.w,0.0,0.0);", after: `offset*=max(1.0,${MIN}*viewPosition.w/max(width,1e-5));` },
]);
patch(ShaderStore.ShadersStoreWGSL, lineVertexShaderWGSL.name, lineVertexShaderWGSL.shader, [
  { anchor: "var currentScreen: vec2f=", before: CLIP },
  { anchor: "var offset: vec4f= vec4f(normalDir*vertexInputs.normal.w,0.0,0.0);", after: `offset*=max(1.0,${MIN}*viewPosition.w/max(uniforms.width,1e-5));` },
]);
